import React, { useMemo, useState } from 'react';

// The Check file step of Import SBOM: the parsed tree and the validation (sbom_formats.validate),
// before anything is written to Orcanos. An ERROR blocks Create here AND on the server.

const SEV = { error: ['Error', 'bad'], warning: ['Warning', 'warn'], info: ['Info', 'plain'] };
const RANK = { error: 3, warning: 2, info: 1 };
const MAX_ROWS = 2000;

function worst(list) {
  let w = '';
  (list || []).forEach((i) => { if ((RANK[i.sev] || 0) > (RANK[w] || 0)) w = i.sev; });
  return w;
}

function kidsOf(edges) {
  const m = {};
  (edges || []).forEach(([p, c]) => { (m[p || ''] = m[p || ''] || []).push(c); });
  return m;
}

/** Depth-first rows; a node under two parents is two rows (keyed by path); loops are cut. */
function flatten(kids, byRef, open, keep) {
  const rows = [];
  const walk = (ref, depth, path) => {
    (kids[ref] || []).forEach((ch) => {
      if (!byRef[ch] || path.includes(ch) || (keep && !keep.has(ch))) return;
      const p = [...path, ch];
      const id = p.join('/');
      const has = (kids[ch] || []).some((g) => byRef[g] && !p.includes(g) && (!keep || keep.has(g)));
      rows.push({ id, ref: ch, depth, has });
      if (has && (keep || open.has(id)) && rows.length < MAX_ROWS) walk(ch, depth + 1, p);
    });
  };
  walk('', 1, []);
  return rows;
}

export default function SbomCheck({ preview, validation }) {
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(null);
  const comps = useMemo(() => preview?.components || [], [preview]);
  const byRef = useMemo(() => Object.fromEntries(comps.map((c) => [c.ref, c])), [comps]);
  const kids = useMemo(() => kidsOf(preview?.edges), [preview]);
  const issues = useMemo(() => validation?.issues || {}, [validation]);
  const [open, setOpen] = useState(() => new Set((kidsOf(preview?.edges)[''] || [])));

  // A filter or search shows only the matching components and the branches that lead to them.
  const keep = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (filter === 'all' && !t) return null;
    const hit = new Set(comps.filter((c) => {
      const w = worst(issues[c.ref]);
      if (filter === 'error' && w !== 'error') return false;
      if (filter === 'warning' && w !== 'warning' && w !== 'error') return false;
      if (filter === 'ok' && w && w !== 'info') return false;
      return !t || `${c.name} ${c.version} ${c.purl} ${c.supplier}`.toLowerCase().includes(t);
    }).map((c) => c.ref));
    const parents = {};
    (preview?.edges || []).forEach(([p, c]) => { (parents[c] = parents[c] || []).push(p); });
    const out = new Set(hit);
    const stack = [...hit];
    while (stack.length) {
      (parents[stack.pop()] || []).forEach((p) => { if (p && !out.has(p)) { out.add(p); stack.push(p); } });
    }
    return out;
  }, [filter, q, comps, issues, preview]);

  const rows = useMemo(() => flatten(kids, byRef, open, keep), [kids, byRef, open, keep]);
  const counted = useMemo(() => {
    const n = { error: 0, warning: 0, ok: 0 };
    comps.forEach((c) => {
      const w = worst(issues[c.ref]);
      if (w === 'error') n.error += 1; else if (w === 'warning') n.warning += 1; else n.ok += 1;
    });
    return n;
  }, [comps, issues]);

  if (!preview || !validation) return null;
  const toggle = (id) => setOpen((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const expandAll = () => {
    const all = new Set();
    flatten(kids, byRef, { has: () => true }, null).forEach((r) => all.add(r.id));
    setOpen(all);
  };
  const root = preview.root || {};
  const c = sel ? byRef[sel] : null;

  return (
    <>
      <div className={validation.ok ? 'sbom-okbar' : 'sbom-error'} role="status">
        {validation.ok
          ? <><b>Ready to import.</b> {validation.warnings ? `${validation.warnings} warning${validation.warnings === 1 ? '' : 's'} to review; they do not block the import.` : 'No problems found.'}</>
          : <><b>{validation.errors} error{validation.errors === 1 ? '' : 's'}. This file cannot be imported.</b> Fix the file and import it again. Warnings do not block.</>}
      </div>

      <div className="sbom-grid2">
        <div>
          <h4 style={{ margin: '0 0 6px' }}>FDA SBOM elements</h4>
          <table className="sbom-table sbom-checks">
            <tbody>
              <tr><td>Author / tool</td><td>{(validation.doc_issues || []).some((i) => i.text.startsWith('No author')) ? <Pill sev="warning" t="Missing" /> : <Pill t="Present" />}</td></tr>
              <tr><td>Timestamp</td><td>{(validation.doc_issues || []).some((i) => i.text.startsWith('No timestamp')) ? <Pill sev="warning" t="Missing" /> : <Pill t="Present" />}</td></tr>
              <tr><td>Dependency relationships</td><td>{(validation.doc_issues || []).some((i) => i.text.startsWith('No dependency')) ? <Pill sev="warning" t="Flat list" /> : <Pill t="Present" />}</td></tr>
              {validation.checks.map((k) => (
                <tr key={k.id}>
                  <td>{k.label}</td>
                  <td>{k.missing ? <Pill sev={k.severity} t={`Missing on ${k.missing} of ${k.total}`} /> : <Pill t={`All ${k.total}`} />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div>
          <h4 style={{ margin: '0 0 6px' }}>About the file</h4>
          {(validation.doc_issues || []).length === 0
            ? <div className="sbom-tiny sbom-muted">No file-level problems.</div>
            : (validation.doc_issues || []).map((i) => (
              <div key={i.text} className="sbom-issue"><Pill sev={i.sev} t={SEV[i.sev][0]} /><span>{i.text}</span></div>
            ))}
        </div>
      </div>

      <div className="sbom-table-card">
        <div className="sbom-tbar">
          <b>Contents</b>
          <div className="sbom-tabs">
            {[['all', `All ${comps.length}`], ['error', `Errors ${counted.error}`], ['warning', `Warnings ${counted.warning + counted.error}`], ['ok', `No problems ${counted.ok}`]].map(([k, l]) => (
              <button type="button" key={k} className={filter === k ? 'sbom-on' : ''} onClick={() => setFilter(k)}>{l}</button>
            ))}
          </div>
          <span className="sbom-head-sp" style={{ flex: 1 }} />
          <input className="sbom-search" placeholder="Search name, purl, supplier" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search components" />
          {!keep && <>
            <button type="button" className="sbom-btn sbom-btn--ghost" onClick={expandAll}>Expand all</button>
            <button type="button" className="sbom-btn sbom-btn--ghost" onClick={() => setOpen(new Set())}>Collapse all</button>
          </>}
        </div>
        <div className={c ? 'sbom-split sbom-split--check' : ''}>
          <div className="sbom-tw" style={{ maxHeight: 460, overflowY: 'auto' }}>
            <table className="sbom-table">
              <thead><tr><th>Component</th><th>Version</th><th className="sbom-hide-s">Type</th><th className="sbom-hide-s">Supplier</th><th className="sbom-hide-s">Licence</th><th>Check</th></tr></thead>
              <tbody>
                <tr className="sbom-root">
                  <td><div className="sbom-tree"><span className="sbom-glyph sbom-glyph--rel">R</span>{root.name || 'The release'}</div></td>
                  <td className="sbom-mono">{root.version || ''}</td><td className="sbom-hide-s" /><td className="sbom-hide-s" /><td className="sbom-hide-s" /><td />
                </tr>
                {rows.map((r) => {
                  const x = byRef[r.ref];
                  const w = worst(issues[r.ref]);
                  const n = (issues[r.ref] || []).filter((i) => i.sev === w).length;
                  return (
                    <tr key={r.id} className={sel === r.ref ? 'sbom-sel' : ''} onClick={() => setSel(sel === r.ref ? null : r.ref)} style={{ cursor: 'pointer' }}>
                      <td>
                        <div className="sbom-tree">
                          {Array.from({ length: r.depth - 1 }).map((_, i) => <span key={i} className="sbom-rail" />)}
                          <button type="button" className={`sbom-chev${r.has && !keep ? '' : ' sbom-chev--blank'}`} aria-label={open.has(r.id) ? 'Collapse' : 'Expand'}
                            onClick={(e) => { e.stopPropagation(); toggle(r.id); }}>{open.has(r.id) || keep ? '▾' : '▸'}</button>
                          <span className={`sbom-glyph sbom-glyph--${r.depth === 1 ? 'dir' : 'ind'}`}>{(x.type || 'l')[0].toUpperCase()}</span>
                          {x.group ? `${x.group}/` : ''}{x.name}
                        </div>
                      </td>
                      <td className="sbom-mono">{x.version || <span className="sbom-miss">none</span>}</td>
                      <td className="sbom-hide-s sbom-muted">{x.type}</td>
                      <td className="sbom-hide-s">{x.supplier || x.author || <span className="sbom-miss">none</span>}</td>
                      <td className="sbom-hide-s">{(x.licences || []).join(', ') || <span className="sbom-miss">none</span>}</td>
                      <td>{w === 'error' || w === 'warning' ? <Pill sev={w} t={`${n} ${SEV[w][0].toLowerCase()}${n === 1 ? '' : 's'}`} /> : <Pill t="OK" />}</td>
                    </tr>
                  );
                })}
                {!rows.length && <tr><td colSpan={6} className="sbom-empty">Nothing matches.</td></tr>}
              </tbody>
            </table>
            {rows.length >= MAX_ROWS && <div className="sbom-note">Showing the first {MAX_ROWS} rows. Search to find a component.</div>}
          </div>
          {c && (
            <div className="sbom-detail">
              <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                <b>{c.name}</b><span className="sbom-mono sbom-muted">{c.version}</span>
                <button type="button" className="sbom-modal-x" aria-label="Close" onClick={() => setSel(null)}>×</button>
              </div>
              {(issues[c.ref] || []).map((i) => (
                <div key={i.code} className="sbom-issue"><Pill sev={i.sev} t={SEV[i.sev][0]} /><span>{i.text}</span></div>
              ))}
              <dl className="sbom-kv">
                <dt>Package URL</dt><dd className="sbom-mono">{c.purl || '—'}</dd>
                <dt>Type</dt><dd>{c.type || '—'}</dd>
                <dt>Supplier</dt><dd>{c.supplier || c.author || '—'}</dd>
                <dt>Licence</dt><dd>{(c.licences || []).join(', ') || '—'}</dd>
                <dt>Level of support</dt><dd>{c.support_level || '—'}</dd>
                <dt>End of support</dt><dd>{c.end_of_support || '—'}</dd>
                <dt>SOUP class</dt><dd>{c.soup_class || '—'}</dd>
                {Object.entries(c.hashes || {}).map(([a, h]) => <React.Fragment key={a}><dt>{a}</dt><dd className="sbom-mono">{h}</dd></React.Fragment>)}
                {c.url && <><dt>Website</dt><dd className="sbom-mono">{c.url}</dd></>}
                {c.description && <><dt>Description</dt><dd>{c.description}</dd></>}
                <dt>Used by</dt>
                <dd>{(preview.edges || []).filter(([, ch]) => ch === c.ref).map(([p]) => (p ? byRef[p]?.name : (root.name || 'the release'))).join(', ')}</dd>
              </dl>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function Pill({ sev, t }) {
  return <span className={`sbom-pill sbom-pill--${sev ? SEV[sev][1] : 'ok'}`}>{t}</span>;
}
